import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../api/client";
import type { Project, PublishedSnippet, SnippetDeploymentTarget } from "../../types";
import type { SnippetRow } from "./types";

interface SnippetsPageData {
  projects: Project[];
  rows: SnippetRow[];
  targetsBySnippet: Record<string, SnippetDeploymentTarget[]>;
}

// One combined query for the whole page rather than three separate useState slots plus a
// hand-rolled load()/useEffect: projects, their published snippets, and each snippet's
// deployment targets are always consumed together here, so there's no independent caching value
// in splitting them, and deriving all three from a single query result avoids the setState
// cascade (and the cascading re-renders it caused) that a manual loader triggers on every fetch.
async function fetchSnippetsPageData(): Promise<SnippetsPageData> {
  const { data: fetchedProjects } = await axiosInstance.get<Project[]>("/projects");

  // No single endpoint lists all snippets across projects today, so this fetches each project's
  // snippets, then each snippet's deployment targets, in parallel and flattens client-side. Fine
  // for the snippet counts this product deals with (a handful per account); worth revisiting with
  // dedicated endpoints if that stops being true.
  const bySnippet = await Promise.all(
    fetchedProjects.map(async (project) => {
      const { data: snippets } = await axiosInstance.get<PublishedSnippet[]>(
        `/projects/${project.id}/snippets`
      );
      return snippets.map((snippet) => ({ ...snippet, projectName: project.name }));
    })
  );
  const rows = bySnippet.flat();

  const targetEntries = await Promise.all(
    rows.map(async (row) => {
      const { data } = await axiosInstance.get<SnippetDeploymentTarget[]>(
        `/projects/${row.projectId}/snippets/${row.id}/deployments`
      );
      return [row.id, data] as const;
    })
  );

  return {
    projects: fetchedProjects,
    rows,
    targetsBySnippet: Object.fromEntries(targetEntries),
  };
}

export function useSnippetsData() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["snippets-page"],
    queryFn: fetchSnippetsPageData,
  });

  return {
    projects: data?.projects ?? [],
    rows: data?.rows ?? [],
    targetsBySnippet: data?.targetsBySnippet ?? {},
    loading: isFetching,
    refetch,
  };
}
